import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Camera, CameraOff, ShieldCheck, ShieldAlert, X, RefreshCw, Clock } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso?.slice(0, 10) || "—"; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

function StatusBadge({ status, daysRemaining }) {
  const expired = status === "expired";
  const dueSoon = !expired && daysRemaining < 30;
  const cls = expired
    ? "bg-[#E63946]/10 text-[#E63946] border-[#E63946]/30"
    : dueSoon
      ? "bg-[#C89B3C]/15 text-[#A0741F] border-[#C89B3C]/40"
      : "bg-[#1F7A3D]/10 text-[#1F7A3D] border-[#1F7A3D]/30";
  const Icon = expired ? ShieldAlert : ShieldCheck;
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-[0.16em] ${cls}`} data-testid="verify-status-badge">
      <Icon className="h-3.5 w-3.5" />
      {expired ? "Expired" : (status || "active")}
      {!expired && <span className="font-medium normal-case tracking-normal opacity-80">· {daysRemaining}d left</span>}
    </span>
  );
}

export default function AdminVerify() {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);            // { found, membership, ... }
  const [scanning, setScanning] = useState(false);
  const [logs, setLogs] = useState([]);
  const scannerRef = useRef(null);
  const containerId = "qr-reader";

  const refreshLogs = async () => {
    try {
      const r = await api.get("/admin/verify-logs?page=1&limit=20");
      setLogs(r.data.items || []);
    } catch (e) { console.error("verify-logs fetch failed", e); /* non-critical */ }
  };
  useEffect(() => { refreshLogs(); }, []);

  const lookup = async (q, method = "manual") => {
    if (!q || !q.trim()) return toast.error("Enter Member ID, Mobile or Email");
    setBusy(true); setResult(null);
    try {
      const r = await api.get(`/admin/verify-member`, { params: { q: q.trim(), method } });
      setResult(r.data);
      if (!r.data.found) toast.error("No matching member found");
      refreshLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };

  const startScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const html5 = new Html5Qrcode(containerId);
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
        async (decoded) => {
          await stopScan();
          setQuery(decoded);
          await lookup(decoded, "qr");
        },
        () => { /* per-frame failure callback — silently ignore */ },
      );
    } catch (e) {
      toast.error("Camera unavailable. Use manual search instead.");
      setScanning(false);
    }
  };
  const stopScan = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (e) { console.error("QR scanner stop failed", e); /* non-critical */ }
    setScanning(false);
  };
  useEffect(() => () => { stopScan(); }, []);   // cleanup on unmount

  const m = result?.membership;
  const matchedSub = result?.matched_member;

  return (
    <div className="space-y-6" data-testid="admin-verify-page">
      <div>
        <h2 className="font-display text-3xl font-bold text-[#0F2540]">Verify Member</h2>
        <p className="text-sm text-[#5C6B7A] mt-1">Confirm a walk-in's NPC validity by Member ID, Mobile, Email — or scan their card's QR.</p>
      </div>

      {/* Search bar + QR toggle */}
      <div className="rounded-3xl border border-[#E1E8F0] bg-white p-5">
        <form onSubmit={(e) => { e.preventDefault(); lookup(query); }} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6B7A]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="NPC001234 · NPC-M001234 · 9876543210 · email"
              className="w-full pl-10 pr-3 py-3 rounded-xl border border-[#E1E8F0] focus:border-[#1E4F87] outline-none text-sm"
              data-testid="verify-search-input"
              autoFocus
            />
          </div>
          <button type="submit" className="btn-primary !px-6" disabled={busy} data-testid="verify-submit">
            {busy ? "Verifying…" : "Verify"}
          </button>
          <button type="button" onClick={scanning ? stopScan : startScan}
            className={`btn-ghost !px-4 ${scanning ? "!border-[#E63946] !text-[#E63946]" : ""}`}
            data-testid="verify-scan-toggle">
            {scanning ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
            {scanning ? "Stop scan" : "Scan QR"}
          </button>
        </form>
        <div id={containerId} className={`mt-4 rounded-xl overflow-hidden ${scanning ? "block" : "hidden"}`} style={{ minHeight: scanning ? 280 : 0 }} data-testid="qr-reader-region" />
      </div>

      {/* Result card */}
      {result && result.found && m && (
        <div className="rounded-3xl border border-[#E1E8F0] bg-white overflow-hidden" data-testid="verify-result-card">
          <div className="bg-gradient-to-br from-[#0F2540] to-[#1E4F87] text-white p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold opacity-80">Primary member</div>
                <div className="font-display text-2xl font-bold mt-1">{m.full_name}</div>
                <div className="text-sm opacity-80 mt-1 font-mono">{m.member_id} · {m.membership_number}{m.uhid ? ` · UHID ${m.uhid}` : ""}</div>
              </div>
              <StatusBadge status={result.effective_status} daysRemaining={result.days_remaining} />
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
              <Info label="Plan" value={m.plan_name} />
              <Info label="Valid till" value={fmtDate(m.valid_till)} />
              <Info label="Members" value={`${m.total_members || (m.members || []).length || 1} / ${m.family_members_allowed || 1}`} />
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Matched specific member (if sub-member) */}
            {matchedSub && (
              <div className="rounded-2xl border-2 border-[#A53A2A]/30 bg-[#FFF8F2] p-4" data-testid="verify-matched-sub">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#A53A2A] font-bold">Matched member</div>
                <div className="mt-1 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-bold text-[#0F2540] text-lg">{matchedSub.full_name}</div>
                    <div className="text-xs text-[#5C6B7A]">{matchedSub.relation || "—"} · {matchedSub.mobile || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <KycBadge status={matchedSub.kyc_status} testid={`verify-matched-kyc-${matchedSub.member_id}`} />
                    <div className="font-mono text-xs text-[#0F2540]">{matchedSub.member_id}{matchedSub.uhid ? ` · UHID ${matchedSub.uhid}` : ""}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact / KYC */}
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Info label="Email" value={m.email} mono />
              <Info label="Mobile" value={m.mobile} mono />
              <Info label="UHID" value={m.uhid || "—"} mono />
              <Info label="Primary KYC" value={(m.kyc_status || "pending").toUpperCase()} />
              <Info label="Address" value={m.address || "—"} className="sm:col-span-2" />
            </div>

            {/* All linked members + KYC overview (shown for every membership, not just families
                so admins can see status when verifying via membership ID/number). */}
            {Array.isArray(m.members) && m.members.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-[#5C6B7A] mb-2">All members on this membership</h4>
                <div className="grid sm:grid-cols-2 gap-2">
                  {m.members.map((sm, i) => (
                    <div key={i} className="rounded-xl border border-[#E1E8F0] p-3 text-sm" data-testid={`verify-fam-${i}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="font-semibold text-[#0F2540]">{sm.full_name}{sm.is_primary && <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1E4F87]/10 text-[#1E4F87] font-bold">Primary</span>}</div>
                        <KycBadge status={sm.kyc_status} testid={`verify-kyc-${sm.member_id}`} />
                      </div>
                      <div className="text-xs text-[#5C6B7A] mt-1">{sm.relation || "—"} · {sm.mobile || "—"}</div>
                      <div className="text-[10px] font-mono text-[#5C6B7A] mt-0.5">{sm.member_id}{sm.uhid ? ` · UHID ${sm.uhid}` : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-[11px] text-[#5C6B7A]">Matched via <b>{result.matched_via}</b> · {fmtDateTime(new Date().toISOString())}</div>
          </div>
        </div>
      )}

      {result && !result.found && (
        <div className="rounded-3xl border-2 border-dashed border-[#E63946]/40 bg-[#E63946]/5 p-8 text-center" data-testid="verify-not-found">
          <ShieldAlert className="h-10 w-10 text-[#E63946] mx-auto" />
          <h3 className="mt-3 font-display text-xl font-bold text-[#0F2540]">No matching member</h3>
          <p className="text-sm text-[#5C6B7A] mt-1">Check the ID/mobile/email and try again, or rescan the QR.</p>
          <button onClick={() => { setResult(null); setQuery(""); }} className="btn-ghost mt-4 !text-xs" data-testid="verify-reset-btn">
            <X className="h-3 w-3" /> Clear
          </button>
        </div>
      )}

      {/* Recent logs */}
      <div className="rounded-3xl border border-[#E1E8F0] bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E1E8F0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#5C6B7A]" />
            <h4 className="text-sm font-semibold text-[#0F2540]">Recent verifications</h4>
          </div>
          <button onClick={refreshLogs} className="text-xs text-[#1E4F87] underline inline-flex items-center gap-1" data-testid="verify-refresh-logs">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F7FB] text-[#5C6B7A] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">Query</th>
                <th className="text-left px-4 py-2">Method</th>
                <th className="text-left px-4 py-2">Result</th>
                <th className="text-left px-4 py-2">By</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.log_id} className="border-t border-[#E1E8F0] hover:bg-[#F4F7FB]/40" data-testid={`verify-log-${l.log_id}`}>
                  <td className="px-4 py-2 text-xs text-[#5C6B7A]">{fmtDateTime(l.created_at)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{l.query?.slice(0, 64)}</td>
                  <td className="px-4 py-2 text-xs">{l.method}</td>
                  <td className="px-4 py-2 text-xs">
                    {l.matched
                      ? <span className="text-[#1F7A3D] font-bold">✓ {l.member_id || l.membership_id}</span>
                      : <span className="text-[#E63946] font-bold">✗ Not found</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-[#5C6B7A]">{l.actor_username || l.actor_email || "—"}{l.actor_username && l.actor_email ? <span className="block opacity-60">{l.actor_email}</span> : null}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#5C6B7A]">No verifications yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono = false, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">{label}</div>
      <div className={`mt-0.5 font-semibold ${mono ? "font-mono text-sm" : "text-sm"}`}>{value || "—"}</div>
    </div>
  );
}

const KYC_BADGE_STYLES = {
  verified: "bg-[#1F7A3D]/15 text-[#1F7A3D] border border-[#1F7A3D]/30",
  rejected: "bg-[#E63946]/15 text-[#E63946] border border-[#E63946]/30",
  failed:   "bg-[#E63946]/15 text-[#E63946] border border-[#E63946]/30",
  pending:  "bg-[#A53A2A]/15 text-[#A53A2A] border border-[#A53A2A]/30",
};
function KycBadge({ status, testid }) {
  const s = (status || "pending").toLowerCase();
  const cls = KYC_BADGE_STYLES[s] || KYC_BADGE_STYLES.pending;
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full font-bold ${cls}`} data-testid={testid}>
      KYC · {s}
    </span>
  );
}
